/**
 * RecruitCRM's stage vocabulary as CRM pipelines: the stages of the pipelines `seedRcrmPipelines`
 * (`@crm/db`) adds to an organisation's Sales Flow, Job Flow, Candidate Flow and Shortlist Flow types (named by
 * `RCRM_PIPELINE_NAMES`, each becoming its type's default), so a CRM stage and the RCRM stage it
 * stands for carry the same name (and slug) and the sync needs no guesswork.
 *
 * Source: the Data team's `rcrm_entity_stages.xlsx` (2026-10-28), every stage name found in
 * `rl-data-platform-dev.silver.recruitcrm_{deals,jobs,candidates}` (`deal_stage`, `job_status`,
 * `candidate_stage`), spelled exactly as RCRM stores them; Shortlist Flow's list is not in the
 * sheet but was read from the RCRM API (hiring stages of pipeline 0, 2026-11-01). The sheet lists
 * them alphabetically;
 * the ORDER here is the funnel, and which stages END the journey (won / lost) is our reading of
 * the names. Both are the admins' to change afterwards, like any stage.
 *
 * - RCRM deals are the sales funnel with a client, which in the CRM is a project in Sales Flow
 *   (a CRM deal is a payment, so Deal Flow keeps its default). "Won" is where RCRM counts a win.
 *   The sheet's 7 deal stages that RCRM's deals pipeline lacks are left out
 *   (`RCRM_RETIRED_PIPELINE_STAGES`).
 * - RCRM candidate stages go to Candidate Flow, the candidate's own flow. Until TS-1888 that
 *   type (then `shortlist_candidate`, "Candidate Stage") was also a candidate's stage on a
 *   shortlist; shortlist places now use Shortlist Flow, which gets RCRM's hiring pipeline stages
 *   (a candidate's stage on a job): "Master Hiring Pipeline", id 0, the only one RCRM has.
 * - RCRM contacts' four stages (Active Client, Canceled, Closed, Prospect) have no CRM pipeline;
 *   companies and placements have none in RCRM either.
 */
export const RCRM_PIPELINE_TYPES = [
    'project',
    'job',
    'candidate',
    'shortlist',
];
/**
 * How the seed's pipelines are named, and how it recognises one it made on an earlier run: a
 * pipeline of the type whose name starts with this (any case), whatever an admin wrote after it.
 */
export const RCRM_PIPELINE_PREFIX = 'RCRM - ';
/**
 * The seeded pipeline's name in each type: `RCRM_PIPELINE_PREFIX` and the type's label without
 * "Flow", plural where it names records. Pipeline names are unique per type, and each must differ
 * from the label the API names the type's first default after (`PIPELINE_TYPE_META`).
 */
export const RCRM_PIPELINE_NAMES = {
    project: `${RCRM_PIPELINE_PREFIX}Sales`,
    job: `${RCRM_PIPELINE_PREFIX}Jobs`,
    candidate: `${RCRM_PIPELINE_PREFIX}Candidates`,
    shortlist: `${RCRM_PIPELINE_PREFIX}Shortlists`,
};
/**
 * What the seed named its pipelines until 2026-10-31 (TS-1859): a pipeline of the type with this
 * name (any case) is the seed's too, and is renamed to its `RCRM_PIPELINE_NAMES` name. Like those,
 * each must differ from the type's `PIPELINE_TYPE_META` label, or the seed would rename the API's
 * own default.
 */
export const RCRM_LEGACY_PIPELINE_NAMES = {
    project: 'Sales',
    job: 'Jobs',
    candidate: 'Candidates',
    shortlist: 'Shortlists',
};
/**
 * Stages the seed once gave its pipeline of a type and no longer does (TS-1878, the user,
 * 2026-10-31): the 7 RCRM deal stages of the sheet that RecruitCRM's deals pipeline does not have
 * (its 17 live stages, read in production on 2026-10-31), so a project moved to one could never
 * move its RCRM deal. `seedRcrmPipelines` soft-deletes them from the pipeline an earlier run made,
 * unless records or a roll-up rule still use them. Job Flow's stages of the same names are RCRM
 * job statuses and stay.
 */
export const RCRM_RETIRED_PIPELINE_STAGES = {
    project: [
        'Sales - Appointment Booked',
        'Hiring Form Submitted',
        'Candidate Vetting Completed - Phase 1',
        'Candidate Vetting Completed',
        'Second Round of Interviews',
        'Interviews Completed: Awaiting Decision',
        'Sales - Met: Sold',
    ],
};
/**
 * Stages added to `RCRM_PIPELINE_STAGES` after pipelines were already seeded with that list: the
 * seed appends each to an earlier run's pipeline that has never held it (by slug, soft-deleted
 * stages included, so a stage an admin removed is not brought back). Each must be in
 * `RCRM_PIPELINE_STAGES[type]`.
 */
export const RCRM_ADDED_PIPELINE_STAGES = {
    // 2026-11-02: RCRM's job pipelines export (DATA-1306) still has places at it.
    shortlist: ['Interview Rescheduled'],
};
const open = (name, colour) => ({ name, colour });
const won = (name, probability) => ({
    name,
    colour: '700',
    terminalKind: 'won',
    ...(probability === undefined ? {} : { probability }),
});
const lost = (name, probability) => ({
    name,
    colour: '200',
    terminalKind: 'lost',
    ...(probability === undefined ? {} : { probability }),
});
export const RCRM_PIPELINE_STAGES = {
    // RCRM deals (15: the sheet's 22 less RCRM_RETIRED_PIPELINE_STAGES): booking → meeting and
    // follow-ups → won → won & fulfilled.
    project: [
        open('Appointment Booked', '300'),
        open('Missed Meeting', '300'),
        open('Missed Meeting - Follow up 2', '300'),
        open('Met', '400'),
        open('Met: Bundles', '400'),
        open('Met: Follow up 1', '400'),
        open('Met: Follow up 2', '400'),
        open('Met: Follow up 3', '400'),
        open('Met: Follow up 4', '400'),
        open('Repeat Client - Hiring Again', '500'),
        won('Won', 100),
        won('Won & Fulfilled', 100),
        lost('Lost', 0),
        lost('Canceled Sales Call', 0),
        lost('Canceled (after signing up)', 0),
    ],
    // RCRM job statuses (29): intake → sourcing and vetting → client interviews → onboarding.
    job: [
        open('New Job', '300'),
        open('Hiring Form Submitted', '300'),
        open('Open', '300'),
        open('Internal: Open', '300'),
        open('Advertise Online', '400'),
        open('Sourcing: Pending', '400'),
        open('Send Interview Invites', '400'),
        open('Candidate Vetting Completed - Phase 1', '400'),
        open('Final Recruiter Approved - Phase 2', '400'),
        open('VA Presentation Scheduled', '500'),
        open('Interview Scheduled', '500'),
        open('Client Missed VA Interview', '500'),
        open('Second Round of Interviews', '500'),
        open('Interviews Completed: Awaiting Decision', '500'),
        open("Can't Fill - Need Help Hiring Manager", '500'),
        open('Onboarding NOT Booked', '600'),
        open('Onboarding Booked', '600'),
        open('Missed Onboarding Meeting', '600'),
        open('Unresponsive Before Onboarding', '600'),
        open('VA <> Client Onboarding w/ AE', '600'),
        open('On Hold', '600'),
        open('On Hold - Follow up', '600'),
        won('Closed'),
        won('Closed - Split Payment (Remove once complete)'),
        won('Fermé'),
        won('Internal: Closed'),
        lost('Canceled'),
        lost('Canceled - Before Onboarding'),
        lost('DNC Canceled'),
    ],
    // RCRM candidate stages (24): application and sourcing → phases 1–3 → offer → placed.
    candidate: [
        open('Incomplete Application', '300'),
        open('Sourcing: Waiting for Application', '300'),
        open('Sourcing: Pending', '300'),
        open('Sourcing: Interview', '300'),
        open('Phase 1: Invited to Interview', '400'),
        open('Invite Attempt 2 (Email)', '400'),
        open('Invite Attempt 3 (WhatsApp)', '400'),
        open('Phase 2: Interview Booked', '500'),
        open('Missed Interview', '500'),
        open('Phase 3: Passed', '600'),
        open('Internal: Vetting', '600'),
        open('Internal', '600'),
        open('On Hold', '600'),
        open('WO Extended', '600'),
        won('Placed'),
        won('Internal: Hired'),
        lost('Failed Interview'),
        lost('Missed Interview 2nd Attempt (final)'),
        lost('Disqualified: Check Notes'),
        lost('Failed'),
        lost('Re-Vetting Failed'),
        lost('Internal: Not qualified'),
        lost('Hired by Another Company'),
        lost('Fired'),
    ],
    // RCRM's hiring pipeline, "Master Hiring Pipeline" (id 0; stage ids in comments), read from the
    // API on 2026-11-01: applied → invited → attended → placed.
    shortlist: [
        open('Applied', '300'), // 10
        open('Assigned', '300'), // 1
        open('Confirmed by Hiring Manager', '400'), // 426566
        open('Invited to Interview', '400'), // 426567
        // Retired in RCRM's hiring pipeline (its API no longer lists it), but 1,191 job places still
        // held it in the DATA-1306 export (2026-11-02): kept as an open stage (the user's decision).
        open('Interview Rescheduled', '400'),
        open('Applicant Confirmed Attendance', '500'), // 426568
        open('Applicant Attended Client Interview', '500'), // 426571
        open('Applicant 2nd Interview Phase', '600'), // 426572
        won('Placed'), // 8
        lost('Applicant Missed Client Interview'), // 426569
        lost('Applicant Failed Client Interview'), // 426570
    ],
};
//# sourceMappingURL=stages.js.map